/**
 * Notifications read-state store (web) — a per-device set of "read" ids.
 *
 * The web twin of apps/mobile/src/notifications/readState.ts. GET
 * /me/notifications is a READ-ONLY view over audit_log with no server-side
 * read/unread state, so we track which rows the user has opened LOCALLY in
 * localStorage. A notification id is the audit_log row id (a stable
 * bigserial-as-string), so it's a safe key.
 *
 * Soft-capped at MAX_TRACKED most-recently-marked ids. Cross-device read sync
 * would need a server-side `notification_reads` table + a mark-read endpoint;
 * out of scope here (see the mobile module's doc comment).
 */

const READ_IDS_KEY = 'fse_notifications_read_ids';
const MAX_TRACKED = 1000;

// Tiny pub-sub so the app-bar unread badge updates live when the inbox marks
// things read (read state lives in localStorage, not React state, so the header
// can't observe it without a signal). Listeners fire on every markRead/markMany.
type Listener = () => void;
const listeners = new Set<Listener>();

/** Subscribe to read-state changes; returns an unsubscribe fn. */
export function subscribeReadState(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function notify(): void {
  for (const l of listeners) l();
}

export function getReadIds(): Set<string> {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return new Set();
    const raw = window.localStorage.getItem(READ_IDS_KEY);
    if (!raw) return new Set();
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((x): x is string => typeof x === 'string'));
  } catch {
    return new Set();
  }
}

function persist(ids: Set<string>): void {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    const arr = Array.from(ids);
    const capped = arr.length > MAX_TRACKED ? arr.slice(arr.length - MAX_TRACKED) : arr;
    window.localStorage.setItem(READ_IDS_KEY, JSON.stringify(capped));
  } catch {
    /* non-fatal — read state is a convenience, not a source of truth */
  }
}

export function markRead(id: string): Set<string> {
  const ids = getReadIds();
  ids.add(id);
  persist(ids);
  notify();
  return ids;
}

export function markManyRead(newIds: readonly string[]): Set<string> {
  const ids = getReadIds();
  for (const id of newIds) ids.add(id);
  persist(ids);
  notify();
  return ids;
}
